import { Stack, Text } from "@flpstudio/design-system";
import parseHtml from "html-react-parser";
import DOMPurify from "isomorphic-dompurify";
import { useEffect } from "react";

import { Like } from "@/components/molecules/Like/Like";
import {
  sendDocumentLikeEvent,
  sendDocumentViewEvent,
  sendExpertViewEvent,
} from "@/components/organisms/Document/utils";
import { UserProfile } from "@/components/organisms/UserProfile/UserProfile";
import { useAuth } from "@/hooks/use-auth";
import { usePublishedDocument } from "@/hooks/use-documents";
import { formatDate } from "@/utils/format-date";
import type { SDocument } from "types";

export function DocumentMeta({
  document,
  showSummary = false,
}: { document: SDocument; showSummary?: boolean }) {
  const { user } = useAuth();
  useEffect(() => sendDocumentViewEvent({ document, user }), [document, user]);

  // TODO: init usePublishedDocument with initial data from loader data once we
  // can make authenticated requests in Remix loader
  const { data: docWithLatestLikes } = usePublishedDocument(document.urlTitle);

  return (
    <Stack>
      <Text className="line-clamp-1 text-[--mantine-color-gray-6] text-sm/normal">
        Published{" "}
        {document.publishedAt ? formatDate(new Date(document.publishedAt)) : ""}
      </Text>
      <UserProfile
        user={document.author}
        onExpertView={() =>
          sendExpertViewEvent({
            document,
            user,
          })
        }
      />
      {showSummary && (
        <span className="line-clamp-3">
          {parseHtml(DOMPurify.sanitize(document.summary || ""))}
        </span>
      )}
      <Like
        count={docWithLatestLikes?.likeCount || document.likeCount}
        groupId={document.likeGroup}
        likeGroupType="DOCUMENT"
        likedByMe={docWithLatestLikes?.likedByMe || document.likedByMe}
        readOnly={!user}
        verbose
        onSubmit={() =>
          sendDocumentLikeEvent({
            document,
            user,
          })
        }
      />
    </Stack>
  );
}
